import { Divider, Typography } from '@frontend/components/ui';
import { UICtx } from '@frontend/sorghum/external-providers';
import {
  GetBillingPlanEnum,
  GetBillingResponseType,
  ModalTypesEnum,
  PaymentStatus,
  Project,
  SocialTypeEnum,
} from '@frontend/sorghum/interface';
import {
  dayjs,
  FACEBOOK_PLAN_DETAILS_EN,
  FACEBOOK_PLAN_DETAILS_ZH,
  INSTAGRAM_PRICING_PAGE_EN,
  INSTAGRAM_PRICING_PAGE_ZH,
  PAYMENT_DETAILS,
  SETTING,
  THREADS_PRICING_PAGE_EN,
  THREADS_PRICING_PAGE_ZH,
  usePath,
} from '@frontend/sorghum/utils';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import { Box, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import i18next from 'i18next';
import { get } from 'lodash';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

export interface BillingInformationProps {
  planTitle: string;
  billing: GetBillingResponseType | undefined;
  price: string;
  period: string;
  project: Project | undefined;
  handleUpgradePlan: () => void;
  isPermissionViewer: boolean;
  handleCancelPlan: () => void;
  /** payment_information 的下期週期（效期內改方案時與現行 period 不同）；用於顯示「將轉為年/月方案」 */
  nextPeriod?: number;
}

const PaymentErrorWrapperStyled = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  background: theme.palette['background']['error'][5],
  padding: '12px 16px',
  gap: '8px',
  borderRadius: '4px',

  '& > div': {
    display: 'flex',
    flexDirection: 'column',
    gap: '9.5px',
  },
}));

const CardWrapperStyled = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  padding: '16px 24px',
  background: theme.palette['grey'][100],
  borderRadius: '12px',
}));

const PlanWrapperStyled = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '12px 0',
}));

const PlanSpanWrapperStyled = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  background: theme.palette['background']['primary'][5],
  padding: '4px 12px',
  borderRadius: '4px',
}));

const DetailButtonStyled = styled(Button)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  width: '100%',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '16px 24px',
  borderRadius: '12px',
  background: theme.palette['grey'][100],
  cursor: 'pointer',
  textTransform: 'none',
  color: theme.palette['grey'][900],

  '&:hover': {
    background: theme.palette['grey'][100],
  },

  '&:disabled': {
    color: theme.palette['grey'][500],
  },
}));

export function BillingInformation({
  planTitle,
  billing,
  price,
  period,
  project,
  handleUpgradePlan,
  isPermissionViewer,
  handleCancelPlan,
  nextPeriod,
}: BillingInformationProps) {
  const [t] = useTranslation();
  const { openModal } = useContext(UICtx);
  const { navigateToProjectPage } = usePath();

  const inPlan =
    billing?.plan === GetBillingPlanEnum.TRIAL ||
    billing?.plan === GetBillingPlanEnum.ADVANCE;

  const isBeta = billing?.plan === GetBillingPlanEnum.BETA;

  const paymentStatus = get(project, 'paymentStatus', PaymentStatus.NORMAL);

  // 訂閱實質存活（含緩衝期各態）：取消入口的統一條件。
  // pendingAdvanced 本身就代表已付費，即使 subscribed 欄位缺漏也不能顯示「升級」（防重複購買）
  const isSubscriptionAlive =
    !!billing?.subscribed ||
    !!billing?.paymentRetrying ||
    !!billing?.paymentRetryStopped ||
    !!billing?.pendingAdvanced;

  return (
    <>
      {paymentStatus === PaymentStatus.ABNORMAL && (
        <PaymentErrorWrapperStyled>
          <ErrorOutlineOutlinedIcon color="error" fontSize="small" />
          <Box>
            <Typography variant="subtitle2" color="error.dark">
              {t('setting.payment.errorTitle')}
            </Typography>
            <Typography variant="body2" color="error.dark">
              {t('setting.payment.errorMessage')}
            </Typography>
          </Box>
        </PaymentErrorWrapperStyled>
      )}
      <CardWrapperStyled>
        <PlanWrapperStyled>
          <Box display="flex" flexDirection="column" gap="8px">
            <Typography variant="h5" color="grey.800">
              {planTitle}
            </Typography>
            {/* 已付款待生效（續扣重試成功）時，期間顯示已付費的下期，而非緩衝列日期 */}
            {inPlan && billing?.pendingAdvanced ? (
              <Typography variant="body2" color="grey.600">
                {dayjs(billing.pendingAdvanced.startedAt)
                  .tz()
                  .format('YYYY/MM/DD')}{' '}
                -{' '}
                {dayjs(billing.pendingAdvanced.endedAt)
                  .tz()
                  .format('YYYY/MM/DD')}
              </Typography>
            ) : (
              inPlan &&
              billing?.startedAt &&
              billing?.endedAt &&
              !isBeta && (
                <Typography variant="body2" color="grey.600">
                  {dayjs(billing?.startedAt).tz().format('YYYY/MM/DD')} -{' '}
                  {dayjs(billing?.endedAt).tz().format('YYYY/MM/DD')}
                </Typography>
              )
            )}
          </Box>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'end',
            }}
          >
            {inPlan || isBeta ? (
              <>
                <Typography variant="h5" color="grey.900">
                  {price}
                </Typography>
                <Typography variant="body2" color="grey.600">
                  {period}
                </Typography>
              </>
            ) : (
              <Typography variant="body2" color="grey.900" alignSelf="center">
                -
              </Typography>
            )}
          </Box>
        </PlanWrapperStyled>
        {billing?.plan === GetBillingPlanEnum.ADVANCE && (
          <>
            {billing?.subscribed && billing?.endedAt && (
              <PlanSpanWrapperStyled>
                <Typography variant="caption" color="grey.800">
                  {/* 效期內已改方案（下期週期 ≠ 現行週期）：明示轉換，否則客戶會以為沒買成而重買（2026-09 客訴） */}
                  {nextPeriod && billing?.period && nextPeriod !== billing.period
                    ? t('setting.payment.renewChangeTo', {
                        date: dayjs(billing?.endedAt).tz().format('YYYY/MM/DD'),
                        method:
                          nextPeriod === 2
                            ? t('setting.upgrade.step1.yearly')
                            : t('setting.upgrade.step1.monthly'),
                      })
                    : t('setting.payment.renew', {
                        date: dayjs(billing?.endedAt).tz().format('YYYY/MM/DD'),
                      })}
                </Typography>
              </PlanSpanWrapperStyled>
            )}
            {!billing?.subscribed && billing?.cancelledAt && !isBeta && (
              <PlanSpanWrapperStyled>
                <Typography variant="caption" color="grey.800">
                  {t('setting.payment.cancelOn', {
                    date: dayjs(billing?.cancelledAt).tz().format('YYYY/MM/DD'),
                  })}
                </Typography>
              </PlanSpanWrapperStyled>
            )}
          </>
        )}
        {/* 續扣緩衝期（plan=TRIAL）的補充說明：已付款待生效／付款重試中（2026-08 客訴 UX 修正） */}
        {billing?.plan === GetBillingPlanEnum.TRIAL &&
          billing?.pendingAdvanced && (
            <PlanSpanWrapperStyled>
              <Typography variant="caption" color="grey.800">
                {t('setting.payment.pendingNote', {
                  date: dayjs(billing.pendingAdvanced.startedAt)
                    .tz()
                    .format('YYYY/MM/DD'),
                })}
              </Typography>
            </PlanSpanWrapperStyled>
          )}
        {billing?.plan === GetBillingPlanEnum.TRIAL &&
          !billing?.pendingAdvanced &&
          billing?.paymentRetrying && (
            <PlanSpanWrapperStyled>
              <Typography variant="caption" color="grey.800">
                {t('setting.payment.retryingNote')}
              </Typography>
            </PlanSpanWrapperStyled>
          )}
        {billing?.plan === GetBillingPlanEnum.TRIAL &&
          !billing?.pendingAdvanced &&
          !billing?.paymentRetrying &&
          billing?.paymentRetryStopped && (
            <PlanSpanWrapperStyled>
              <Typography variant="caption" color="grey.800">
                {t('setting.payment.retryStoppedNote', {
                  date: billing?.endedAt
                    ? dayjs(billing.endedAt).tz().format('YYYY/MM/DD')
                    : '-',
                })}
              </Typography>
            </PlanSpanWrapperStyled>
          )}
        {!isBeta && (
          <>
            <Divider />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'end',
                gap: '16px',
                // 付款異常＋緩衝期會有三顆按鈕，小螢幕需換行避免溢出卡片（Codex review P2）
                flexWrap: 'wrap',
              }}
            >
              {paymentStatus === 2 ? (
                <>
                  {/* 付款異常期間訂閱其實還活著，要保留取消入口
                      （2026-08 客訴：緩衝期找不到取消鈕，客戶誤按升級恐重複購買） */}
                  {isSubscriptionAlive && (
                    <Button
                      id="cancel"
                      onClick={handleCancelPlan}
                      disabled={isPermissionViewer}
                      sx={{ textTransform: 'capitalize' }}
                    >
                      {t('common.cancel')}
                    </Button>
                  )}
                  <Button
                    id="upgrade"
                    onClick={handleUpgradePlan}
                    disabled={isPermissionViewer}
                    sx={{ textTransform: 'capitalize' }}
                  >
                    {t('setting.payment.updatePlan')}
                  </Button>
                  <Button
                    id="change_payment"
                    variant="contained"
                    onClick={() =>
                      openModal(ModalTypesEnum.CHANGE_PAYMENT_METHOD)
                    }
                    disabled={isPermissionViewer}
                    sx={{ textTransform: 'capitalize' }}
                  >
                    {t('setting.payment.changePayment')}
                  </Button>
                </>
              ) : (
                <Button
                  id={isSubscriptionAlive ? 'cancel' : 'upgrade'}
                  sx={{ textTransform: 'capitalize' }}
                  variant={isSubscriptionAlive ? 'text' : 'contained'}
                  onClick={
                    isSubscriptionAlive ? handleCancelPlan : handleUpgradePlan
                  }
                  disabled={isPermissionViewer}
                >
                  {isSubscriptionAlive
                    ? t('common.cancel')
                    : t('setting.payment.upgrade')}
                </Button>
              )}
            </Box>
          </>
        )}
      </CardWrapperStyled>
      {!isBeta && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '16px',
          }}
        >
          <DetailButtonStyled
            onClick={() => {
              navigateToProjectPage({ path: `/${SETTING}/${PAYMENT_DETAILS}` });
            }}
            disableRipple
            disabled={isPermissionViewer}
          >
            <Typography variant="body2">
              {t('setting.payment.detail.payment')}
            </Typography>
            <ArrowForwardIosIcon fontSize="tiny" />
          </DetailButtonStyled>
          <DetailButtonStyled
            onClick={() => {
              if (project) {
                switch (project.socialType) {
                  case SocialTypeEnum.FACEBOOK: {
                    window.open(
                      i18next.language.includes('zh')
                        ? FACEBOOK_PLAN_DETAILS_ZH
                        : FACEBOOK_PLAN_DETAILS_EN,
                      '_blank',
                    );
                    break;
                  }
                  case SocialTypeEnum.THREADS: {
                    window.open(
                      i18next.language.includes('zh')
                        ? THREADS_PRICING_PAGE_ZH
                        : THREADS_PRICING_PAGE_EN,
                      '_blank',
                    );
                    break;
                  }
                  case SocialTypeEnum.INSTAGRAM: {
                    window.open(
                      i18next.language.includes('zh')
                        ? INSTAGRAM_PRICING_PAGE_ZH
                        : INSTAGRAM_PRICING_PAGE_EN,
                      '_blank',
                    );
                    break;
                  }
                }
              }
            }}
            disableRipple
          >
            <Typography variant="body2">
              {t('setting.payment.detail.plan')}
            </Typography>
            <ArrowForwardIosIcon fontSize="tiny" />
          </DetailButtonStyled>
        </Box>
      )}
    </>
  );
}

export default BillingInformation;
